/**
 * csvImportHelper.ts
 *
 * Parses CSV files and converts rows into the asset-node JSON structure.
 * CSV format: comma-separated values with header row
 * 
 * Expected columns: asset_name, asset_ip, asset_status, component_name, component_status, etc.
 * (same format as Excel row-wise parser output)
 */

import * as uuid from 'uuid';
import {
  normalizeHeader,
  statusToEnabled,
  toStr,
} from './excelImportCommon';

import type { ImportResult, ImportedAsset, ImportedComponent } from './excelImportHelper';

const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === ',' && !insideQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

const parseCSVContent = (content: string): { headers: string[]; rows: Record<string, string>[] } => {
  const lines = content.split(/\r?\n/).filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  const headers = parseCSVLine(lines[0]).map((h) => normalizeHeader(h.trim()));

  if (headers.length === 0) {
    throw new Error('No headers found in CSV');
  }

  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row: Record<string, string> = {};

    headers.forEach((header, idx) => {
      row[header] = values[idx] ?? '';
    });

    rows.push(row);
  }

  return { headers, rows };
};

export const parseCSVToAssets = async (buffer: ArrayBuffer): Promise<ImportResult> => {
  try {
    const decoder = new TextDecoder('utf-8');
    const content = decoder.decode(buffer);

    const { headers, rows } = parseCSVContent(content);

    if (rows.length === 0) {
      return {
        assets: [],
        errors: ['No data rows found in CSV'],
      };
    }

    // Check for required headers
    const requiredHeaders = ['asset_name', 'asset_ip', 'component_name'];
    const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

    if (missingHeaders.length > 0) {
      return {
        assets: [],
        errors: [
          `Missing required columns: ${missingHeaders.join(', ')}. ` +
          `Required: ${requiredHeaders.join(', ')}`,
        ],
      };
    }

    const assets: ImportedAsset[] = [];
    const assetMap = new Map<string, ImportedAsset>();
    const errors: string[] = [];

    rows.forEach((row, rowIndex) => {
      const assetName = toStr(row.asset_name);
      const assetIp = toStr(row.asset_ip);
      const assetStatus = toStr(row.asset_status).toLowerCase();
      const componentName = toStr(row.component_name);
      const componentStatus = toStr(row.component_status).toLowerCase();

      // Validate asset row
      if (!assetName) {
        errors.push(`Row ${rowIndex + 2}: asset_name is required`);
        return;
      }

      if (!assetIp) {
        errors.push(`Row ${rowIndex + 2}: asset_ip is required`);
        return;
      }

      if (!componentName) {
        errors.push(`Row ${rowIndex + 2}: component_name is required`);
        return;
      }

      const assetKey = `${assetName}|${assetIp}`;

      // Get or create asset
      let asset = assetMap.get(assetKey);

      if (!asset) {
        asset = {
          id: `asset-${uuid.v4()}`,
          type: 'assetNode',
          data: {
            name: assetName,
            ip: assetIp,
            enabled: statusToEnabled(assetStatus),
            options: [],
          },
        };
        assetMap.set(assetKey, asset);
        assets.push(asset);
      } else {
        // Update asset status if explicitly set in this row
        if (assetStatus && assetStatus !== 'active' && assetStatus !== 'inactive') {
          asset.data.enabled = statusToEnabled(assetStatus);
        }
      }

      // Create component from row
      const componentData: Record<string, any> = {};
      const dynamicColumns = headers.filter(
        (h) => !['asset_name', 'asset_ip', 'asset_status', 'component_name', 'component_status', 'component_type', 'component_description'].includes(h)
      );

      dynamicColumns.forEach((col) => {
        const value = row[col];
        if (value && value.trim().length > 0) {
          componentData[col] = value;
        }
      });

      const component: ImportedComponent = {
        id: `component-${uuid.v4()}`,
        component_name: componentName,
        label: componentName,
        enabled: statusToEnabled(componentStatus),
        data: componentData,
      };

      // Avoid duplicate components in same asset
      const existingComponent = asset.data.options.find(
        (opt) => opt.component_name.toLowerCase() === componentName.toLowerCase()
      );

      if (!existingComponent) {
        asset.data.options.push(component);
      }
    });

    return {
      assets,
      errors,
    };
  } catch (err: any) {
    return {
      assets: [],
      errors: [err?.message ?? 'Unknown CSV parsing error'],
    };
  }
};
